'use client';

import { fetchRankingProductList } from '@/api/product';
import type { ListProduct } from '@/api/types/public';
import type { CompleteTrackingParams } from '@/app/(home-page)/types';
import AccessiblePrice from '@/components/AccessiblePrice';
import Image from '@/components/Image';
import { useJump } from '@/components/MagicJump';
import type { Product } from '@/components/Product/ProductCard';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useProductImages } from '@/hooks/useProductImages';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EProductImageType } from '@/utils/enums/goods';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { serializeKey } from '@/utils/swr/keys';
import { RightArrowFineOutlined } from '@zeelool/icon';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import useSWR from 'swr';

import type { BestsellerListConfig, Bestsellers2026Content, Bestsellers2026Props } from './types';
import rankingLabelIcon from './assets/ranking-label.svg';

const PRODUCT_IMAGE_RULE = [EProductImageType.PRODUCT];
const NO_HOVER_IMAGE_RULE: EProductImageType[] = [];
const MIN_PRODUCT_COUNT = 2;
const PRODUCT_STAGE_CLASS_NAME =
  'grid w-full grid-cols-[200px_366px_200px] items-center justify-center gap-[64px] px-[48px] py-[24px] xl:grid-cols-[231px_446px_231px] xl:gap-[92px] 3xl:grid-cols-[359.082px_638px_359.082px] 3xl:gap-[140px]';
const SIDE_PRODUCT_VISUAL_CLASS_NAME =
  'h-[100px] min-w-0 opacity-60 xl:h-[115.795px] 3xl:h-[180px]';
const CENTER_PRODUCT_VISUAL_CLASS_NAME = 'h-[183.574px] min-w-0 xl:h-[223.699px] 3xl:h-[320px]';
const PRODUCT_FOOTER_CLASS_NAME =
  'mx-auto flex w-[366px] items-end justify-between px-[16px] pb-[24px] xl:w-[638px] xl:px-[40px]';
const CAROUSEL_BUTTON_CLASS_NAME =
  'absolute top-[calc(50%-6.29px)] z-10 flex size-[32px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple xl:top-[calc(50%-6.35px)] 3xl:top-[calc(50%-5.5px)]';
const PRODUCT_VISUAL_CONFIG = {
  left: {
    sizes: '(min-width: 1920px) 359px, (min-width: 1280px) 231px, 200px',
    reflectionClassName:
      'top-[82%] h-[18.391%] w-[99.872%] xl:top-[81.635%] xl:h-[18.368%] xl:w-full 3xl:top-[81.633%] 3xl:h-[18.367%]',
    reflectionImageClassName: 'left-[0.05%] top-[-430.47%] h-[610.94%] w-full',
    reflectionGradientClassName: 'to-50%',
  },
  center: {
    sizes: '(min-width: 1920px) 638px, (min-width: 1280px) 446px, 366px',
    reflectionClassName: 'top-[69.375%] h-[30.625%] w-full',
    reflectionImageClassName: 'left-[-0.16%] top-[-226.53%] h-[326.53%] w-[100.31%]',
    reflectionGradientClassName: 'to-50%',
  },
  right: {
    sizes: '(min-width: 1920px) 359px, (min-width: 1280px) 231px, 200px',
    reflectionClassName:
      'top-[76.34%] h-[20.346%] w-[99.4415%] xl:top-[76.525%] xl:h-[20.409%] xl:w-full 3xl:top-[76.533%] 3xl:h-[20.408%]',
    reflectionImageClassName: 'left-[0.06%] top-[-274.37%] h-[488.75%] w-full',
    reflectionGradientClassName: '',
  },
} as const;

type ProductVisualVariant = keyof typeof PRODUCT_VISUAL_CONFIG;

type RankingProduct = ListProduct & {
  business_sku?: string | number;
  sku?: string | number;
  in_stock?: string | number;
  sell_price?: string | number;
  sale_price?: string | number;
  price?: string | number;
  original_price?: string | number;
  market_price?: string | number;
  ranking_tag?: {
    type?: 1 | 2;
    text?: string;
  };
};

function normalizeConfiguredText(value: unknown) {
  if (typeof value !== 'string') return '';
  return value.replace(/<br\s*\/?>/gi, '\n').trim();
}

function normalizePriceValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return typeof value === 'string' ? value.trim() : '';
}

function resolveProductPrice(
  product: RankingProduct,
  userInfo: unknown,
  currency: { name?: string },
) {
  const configuredPrice = priceInfo(userInfo, product, currency) as {
    price?: string | number;
    sell_price?: string | number;
  };
  const sellPrice =
    normalizePriceValue(configuredPrice.sell_price) ||
    normalizePriceValue(configuredPrice.price) ||
    normalizePriceValue(product.sell_price) ||
    normalizePriceValue(product.sale_price) ||
    normalizePriceValue(product.price);
  const originalPrice =
    normalizePriceValue(configuredPrice.price) ||
    normalizePriceValue(product.original_price) ||
    normalizePriceValue(product.market_price) ||
    sellPrice;

  return { price: originalPrice, sell_price: sellPrice };
}

function getTabKey(tab: BestsellerListConfig, index: number) {
  return String(tab.id ?? `${tab.product_group_id ?? 'group'}-${index}`);
}

function isRankingLabel(value: unknown): value is 1 | 2 {
  return value === 1 || value === 2;
}

function isRankingProduct(value: unknown): value is RankingProduct {
  if (!value || typeof value !== 'object') return false;

  const item = value as { id?: unknown; shop_sku?: unknown };
  return Boolean(item.id) && typeof item.shop_sku === 'string' && Boolean(item.shop_sku.trim());
}

function isAvailableProduct(product: RankingProduct) {
  const status = Number(product.status);
  const stock = Number(product.stock);

  if (Number.isFinite(status) && status !== 1) return false;
  if (Number.isFinite(stock) && stock <= 0) return false;
  return String(product.in_stock) !== '2';
}

function buildProductTrackingParams({
  product,
  productIndex,
  moduleIndex,
  indexData,
  content,
  activeTab,
  currency,
  userInfo,
}: {
  product: RankingProduct;
  productIndex: number;
  moduleIndex: number;
  indexData: Bestsellers2026Props['indexData'];
  content: Bestsellers2026Content;
  activeTab: BestsellerListConfig;
  currency: { name?: string; exchange_rate?: string | number };
  userInfo: unknown;
}): CompleteTrackingParams {
  const currentPrice = resolveProductPrice(product, userInfo, currency);
  const productGroup = activeTab.position_jump_product_group;

  return {
    module_index: moduleIndex,
    module_name: indexData.component_id,
    module_id: String(indexData.blocks_advances_id || indexData.id || ''),
    module_title: normalizeConfiguredText(content.layout_title),
    module_tab_name: normalizeConfiguredText(activeTab.list_title),
    mkt_index: productIndex,
    mkt_index_sub: '',
    mkt_jump_type: '2',
    mkt_jump_content: String(product.shop_sku || product.id || ''),
    shop_sku: String(product.shop_sku || ''),
    business_sku: String(product.business_sku || ''),
    product_sku: String(product.sku || ''),
    product_id: String(product.id || ''),
    product_name: String(product.short_name || product.name || ''),
    group_id: String(productGroup?.id || activeTab.product_group_id || ''),
    group_name: productGroup?.name || '',
    product_price: Number(currentPrice?.price || 0),
    current_product_price: Number(currentPrice?.sell_price || 0),
    currency: currency?.name || '',
    dollar_exchange_rate: Number(currency?.exchange_rate || 1),
  };
}

function buildExploreTrackingParams({
  moduleIndex,
  indexData,
  content,
  activeTab,
}: {
  moduleIndex: number;
  indexData: Bestsellers2026Props['indexData'];
  content: Bestsellers2026Content;
  activeTab: BestsellerListConfig;
}): CompleteTrackingParams {
  const productGroup = content.product_group || activeTab.position_jump_product_group;

  return {
    module_index: moduleIndex,
    module_name: indexData.component_id,
    module_id: String(indexData.blocks_advances_id || indexData.id || ''),
    module_title: normalizeConfiguredText(content.layout_title),
    module_tab_name: normalizeConfiguredText(activeTab.list_title),
    mkt_index: 0,
    mkt_index_sub: '',
    mkt_jump_type: String(content.jump_type || ''),
    mkt_jump_content: String(content.Jump_target || ''),
    group_id: String(productGroup?.id || activeTab.product_group_id || ''),
    group_name: productGroup?.name || '',
  };
}

function ProductVisual({
  product,
  variant,
}: {
  product: RankingProduct;
  variant: ProductVisualVariant;
}) {
  const { productImageSrc } = useProductImages({
    productData: product as Product,
    imageRule: PRODUCT_IMAGE_RULE,
    imageHoverRule: NO_HOVER_IMAGE_RULE,
    productImagePriority: 'white-first',
  });
  const isCenter = variant === 'center';
  const productName = normalizeConfiguredText(product.short_name || product.name);
  const visualConfig = PRODUCT_VISUAL_CONFIG[variant];

  if (!productImageSrc) return null;

  return (
    <div
      className="relative h-full w-full"
      aria-hidden={isCenter ? undefined : true}
      data-product-visual={variant}
    >
      <Image
        src={productImageSrc}
        alt={isCenter ? productName : ''}
        fill
        sizes={visualConfig.sizes}
        className="pointer-events-none object-cover mix-blend-multiply"
      />
      <span
        aria-hidden
        data-product-reflection={variant}
        className={`pointer-events-none absolute left-0 overflow-hidden opacity-10 ${visualConfig.reflectionClassName}`}
      >
        <span className="absolute inset-0 -scale-y-100">
          <span
            data-product-reflection-image={variant}
            className={`absolute ${visualConfig.reflectionImageClassName}`}
          >
            <Image
              src={productImageSrc}
              alt=""
              fill
              sizes={visualConfig.sizes}
              className="pointer-events-none object-cover"
            />
          </span>
          <span
            data-product-reflection-gradient={variant}
            className={`absolute inset-0 bg-gradient-to-b from-white to-transparent ${visualConfig.reflectionGradientClassName}`}
          />
        </span>
      </span>
    </div>
  );
}

function SkeletonProductVisual({ variant }: { variant: 'side' | 'center' }) {
  const isCenter = variant === 'center';

  return (
    <div
      data-skeleton-product={variant}
      className={`min-w-0 animate-pulse rounded-[12px] bg-auxiliary-light-gray ${
        isCenter ? CENTER_PRODUCT_VISUAL_CLASS_NAME : SIDE_PRODUCT_VISUAL_CLASS_NAME
      }`}
    />
  );
}

function BestsellersSkeleton({
  rootRef,
  content,
  tabs,
}: {
  rootRef?: (node: HTMLElement | null) => void;
  content: Bestsellers2026Content;
  tabs: BestsellerListConfig[];
}) {
  const heading = normalizeConfiguredText(content.layout_title);
  const showHeading = Number(content.layout_title_status) === 1 && Boolean(heading);
  const exploreText = normalizeConfiguredText(content.btn_text);
  const showExplore = Number(content.view_more_status) === 1 && Boolean(exploreText);
  const showTabList = tabs.length > 1 || Boolean(normalizeConfiguredText(tabs[0]?.list_title));

  return (
    <section
      ref={rootRef}
      className="w-full overflow-hidden bg-primary-white text-[#1a1c1e]"
      style={
        Number(content.bg_color_status) === 1
          ? { backgroundColor: content.bg_color || '#FFFFFF' }
          : undefined
      }
      aria-hidden
    >
      <header className="relative flex w-full items-center justify-between px-[48px] py-[20px] xl:py-[28px]">
        {showTabList ? (
          <div className="flex h-[36px] shrink-0 items-center rounded-[40px] bg-[#f7f7f7] p-[2px] xl:h-[44px]">
            {tabs.map((tab, index) => (
              <span
                key={getTabKey(tab, index)}
                className={`flex h-[32px] items-center justify-center whitespace-nowrap rounded-[40px] px-[24px] font-br text-[14px] leading-[17px] xl:h-[40px] xl:text-[16px] xl:leading-[19px] ${
                  index === 0 ? 'bg-primary-black font-bb text-white' : 'text-[#1a1c1e]'
                }`}
              >
                {normalizeConfiguredText(tab.list_title)}
              </span>
            ))}
          </div>
        ) : (
          <span className="w-[1px]" />
        )}

        {showHeading ? (
          <h2
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-pre-line text-center font-bb text-[24px] leading-[28px] xl:text-[32px] xl:leading-[39px]"
            style={{ color: content.layout_title_color || '#1A1C1E' }}
          >
            {heading}
          </h2>
        ) : null}

        {showExplore ? (
          <span className="flex items-center gap-[8px] font-br text-[12px] leading-[14px] xl:text-[16px] xl:leading-[19px]">
            {exploreText}
            <RightArrowFineOutlined className="text-[20px]" aria-hidden />
          </span>
        ) : (
          <span className="w-[1px]" />
        )}
      </header>

      <div className={PRODUCT_STAGE_CLASS_NAME}>
        <SkeletonProductVisual variant="side" />
        <SkeletonProductVisual variant="center" />
        <SkeletonProductVisual variant="side" />
      </div>

      <div data-skeleton-footer className={PRODUCT_FOOTER_CLASS_NAME}>
        <div className="flex animate-pulse flex-col gap-[8px]">
          <div className="flex flex-col gap-[6px]">
            <span className="h-[19px] w-[112px] rounded-[4px] bg-auxiliary-light-gray" />
            <span className="h-[18px] w-[148px] rounded-[4px] bg-auxiliary-light-gray" />
          </div>
          <span className="h-[20px] w-[132px] rounded-[2px] bg-auxiliary-light-gray" />
        </div>
        <span className="ml-[24px] h-[40px] w-[152px] shrink-0 animate-pulse rounded-[20px] bg-auxiliary-light-gray" />
      </div>
    </section>
  );
}

function Bestsellers2026ContentComponent({
  indexData,
  content,
  tabs,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: Bestsellers2026Props & {
  content: Bestsellers2026Content;
  tabs: BestsellerListConfig[];
}) {
  const t = useTranslations('homePage.bestsellers2026');
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const token = useStore((state) => state.token);
  const toJumpTarget = useJump();
  const reactId = useId();
  const [selectedTabKey, setSelectedTabKey] = useState(() => getTabKey(tabs[0]!, 0));
  const [emptyTabKeys, setEmptyTabKeys] = useState<Set<string>>(() => new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const exposedProductKeys = useRef(new Set<string>());
  const validExposedProductKeys = useRef(new Set<string>());
  const { ref: inViewRef, inView } = useInView({ threshold: 0, triggerOnce: true });

  const visibleTabs = useMemo(
    () => tabs.filter((tab, index) => !emptyTabKeys.has(getTabKey(tab, index))),
    [emptyTabKeys, tabs],
  );
  const activeTabIndex = visibleTabs.findIndex(
    (tab, index) => getTabKey(tab, index) === selectedTabKey,
  );
  const activeTab = visibleTabs[activeTabIndex >= 0 ? activeTabIndex : 0];
  const activeTabKey = activeTab
    ? getTabKey(activeTab, activeTabIndex >= 0 ? activeTabIndex : 0)
    : '';
  const productGroupId = Number(
    activeTab?.product_group_id || activeTab?.position_jump_product_group?.id || 0,
  );
  const rankingLabel = activeTab?.ranking_label;
  const canFetch =
    inView && productGroupId > 0 && isRankingLabel(rankingLabel) && Boolean(activeTabKey);
  const requestKey = canFetch
    ? serializeKey([
        'bestsellers2026',
        indexData.id,
        activeTabKey,
        productGroupId,
        rankingLabel,
        currency?.name,
        Boolean(token),
      ])
    : null;
  const {
    data: productData,
    error: productError,
    isLoading,
  } = useSWR<RankingProduct[] | null>(
    requestKey,
    async () => {
      const response = await fetchRankingProductList({
        group_id: productGroupId,
        ranking_tag: rankingLabel as 1 | 2,
        page: 1,
        limit: activeTab?.list_product_num || 12,
        in_stock: 3,
        stock_sort_first: 1,
      });
      const responseProducts = response.data?.list || response.data?.products;

      if (response.code !== 200 || !Array.isArray(responseProducts)) {
        throw new Error('regularListBestseller 榜单商品接口响应异常');
      }

      return responseProducts.filter(isRankingProduct).filter(isAvailableProduct);
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: 300000,
    },
  );
  const products = productData || [];
  const hasAttemptedFetch = productData !== undefined || Boolean(productError);

  useEffect(() => {
    if (activeTabKey && activeTabKey !== selectedTabKey) {
      setSelectedTabKey(activeTabKey);
      setCurrentIndex(0);
    }
  }, [activeTabKey, selectedTabKey]);

  useEffect(() => {
    if (
      !activeTabKey ||
      productError ||
      productData === undefined ||
      (Array.isArray(productData) && productData.length > 0)
    ) {
      return;
    }

    setEmptyTabKeys((current) => {
      if (current.has(activeTabKey)) return current;
      const next = new Set(current);
      next.add(activeTabKey);
      return next;
    });
  }, [activeTabKey, productData, productError]);

  const safeCurrentIndex = products.length ? currentIndex % products.length : 0;
  const previousIndex = products.length
    ? (safeCurrentIndex - 1 + products.length) % products.length
    : 0;
  const nextIndex = products.length ? (safeCurrentIndex + 1) % products.length : 0;
  const activeProduct = products[safeCurrentIndex];
  const previousProduct = products[previousIndex];
  const nextProduct = products[nextIndex];
  const trackingParams = useMemo(
    () =>
      activeProduct && activeTab
        ? buildProductTrackingParams({
            product: activeProduct,
            productIndex: safeCurrentIndex,
            moduleIndex,
            indexData,
            content,
            activeTab,
            currency,
            userInfo,
          })
        : null,
    [
      activeProduct,
      activeTab,
      content,
      currency,
      indexData,
      moduleIndex,
      safeCurrentIndex,
      userInfo,
    ],
  );

  useEffect(() => {
    if (!inView || !trackingParams || !onExpTrack || !activeProduct) return;

    const productKey = `${activeTabKey}-${String(activeProduct.shop_sku)}`;
    if (exposedProductKeys.current.has(productKey)) return;

    try {
      onExpTrack(trackingParams);
      exposedProductKeys.current.add(productKey);
    } catch (error) {
      logger.error('2026 PC bestsellers exposure tracking error:', error);
    }
  }, [activeProduct, activeTabKey, inView, onExpTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    ts: activeProduct ? `${activeTabKey}-${String(activeProduct.shop_sku)}` : activeTabKey,
    callback: () => {
      if (!trackingParams || !onValidExpTrack || !activeProduct) return;

      const productKey = `${activeTabKey}-${String(activeProduct.shop_sku)}`;
      if (validExposedProductKeys.current.has(productKey)) return;

      try {
        onValidExpTrack(trackingParams);
        validExposedProductKeys.current.add(productKey);
      } catch (error) {
        logger.error('2026 PC bestsellers valid exposure tracking error:', error);
      }
    },
  });
  const sectionRef = useMergedRefs<HTMLElement>(inViewRef, validExposureRef);

  const handleProductClick = useCallback(() => {
    if (!activeProduct || !trackingParams) return;

    try {
      onTrack?.(trackingParams);
    } catch (error) {
      logger.error('2026 PC bestsellers click tracking error:', error);
    }

    toJumpTarget({
      jump_type: 4,
      Jump_target: `/goods-detail/${activeProduct.shop_sku}`,
    });
  }, [activeProduct, onTrack, toJumpTarget, trackingParams]);

  const handleExploreClick = useCallback(() => {
    if (!activeTab) return;

    try {
      onTrack?.(buildExploreTrackingParams({ moduleIndex, indexData, content, activeTab }));
    } catch (error) {
      logger.error('2026 PC bestsellers explore tracking error:', error);
    }

    toJumpTarget(content);
  }, [activeTab, content, indexData, moduleIndex, onTrack, toJumpTarget]);

  const heading = normalizeConfiguredText(content.layout_title);
  const showHeading = Number(content.layout_title_status) === 1 && Boolean(heading);
  const exploreText = normalizeConfiguredText(content.btn_text);
  const showExplore = Number(content.view_more_status) === 1 && Boolean(exploreText);
  const price = activeProduct ? resolveProductPrice(activeProduct, userInfo, currency) : undefined;
  const sellPrice = price?.sell_price
    ? formatPriceNumberWithSymbol(String(price.sell_price), currency)
    : '';
  const originalPrice =
    price?.price && price.price !== price.sell_price
      ? formatPriceNumberWithSymbol(String(price.price), currency)
      : '';
  const rankingText =
    activeProduct && activeTab && activeProduct.ranking_tag?.type === activeTab.ranking_label
      ? normalizeConfiguredText(activeProduct.ranking_tag?.text)
      : '';
  const showTabList =
    visibleTabs.length > 1 || Boolean(normalizeConfiguredText(activeTab?.list_title));

  if (visibleTabs.length === 0) return null;
  if (productError || (hasAttemptedFetch && products.length < MIN_PRODUCT_COUNT)) return null;
  if (!hasAttemptedFetch || isLoading || !activeProduct || !previousProduct || !nextProduct) {
    return <BestsellersSkeleton rootRef={inViewRef} content={content} tabs={visibleTabs} />;
  }

  return (
    <section
      ref={sectionRef}
      data-component-id={indexData.component_id}
      className="relative w-full overflow-hidden bg-primary-white text-[#1a1c1e]"
      style={
        Number(content.bg_color_status) === 1
          ? { backgroundColor: content.bg_color || '#FFFFFF' }
          : undefined
      }
    >
      <header className="relative flex w-full items-center justify-between px-[48px] py-[20px] xl:py-[28px]">
        {showTabList ? (
          <div
            role="tablist"
            aria-labelledby={showHeading ? `${reactId}-heading` : undefined}
            className="flex h-[36px] shrink-0 items-center rounded-[40px] bg-[#f7f7f7] p-[2px] xl:h-[44px]"
          >
            {visibleTabs.map((tab, index) => {
              const tabKey = getTabKey(tab, index);
              const selected = tabKey === activeTabKey;
              const tabLabel = normalizeConfiguredText(tab.list_title);

              return (
                <button
                  key={tabKey}
                  id={`${reactId}-${tabKey}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls={`${reactId}-panel`}
                  className={`h-[32px] cursor-pointer whitespace-nowrap rounded-[40px] px-[24px] text-[14px] leading-[17px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple xl:h-[40px] xl:text-[16px] xl:leading-[19px] ${
                    selected
                      ? 'bg-primary-black font-bb text-white'
                      : 'bg-transparent font-br text-[#1a1c1e]'
                  }`}
                  onClick={() => {
                    setSelectedTabKey(tabKey);
                    setCurrentIndex(0);
                  }}
                >
                  {tabLabel}
                </button>
              );
            })}
          </div>
        ) : (
          <span className="w-[1px]" aria-hidden />
        )}

        {showHeading ? (
          <h2
            id={`${reactId}-heading`}
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-pre-line text-center font-bb text-[24px] leading-[28px] xl:text-[32px] xl:leading-[39px]"
            style={{ color: content.layout_title_color || '#1A1C1E' }}
          >
            {heading}
          </h2>
        ) : null}

        {showExplore ? (
          <button
            type="button"
            className="flex cursor-pointer items-center gap-[8px] bg-transparent p-0 font-br text-[12px] leading-[14px] text-[#1a1c1e] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-purple xl:text-[16px] xl:leading-[19px]"
            onClick={handleExploreClick}
          >
            <span>{exploreText}</span>
            <RightArrowFineOutlined className="text-[20px]" aria-hidden />
          </button>
        ) : (
          <span className="w-[1px]" aria-hidden />
        )}
      </header>

      <div
        id={`${reactId}-panel`}
        role="tabpanel"
        aria-labelledby={
          showTabList
            ? `${reactId}-${activeTabKey}`
            : showHeading
              ? `${reactId}-heading`
              : undefined
        }
        className="relative"
      >
        <div className={PRODUCT_STAGE_CLASS_NAME}>
          <div className={SIDE_PRODUCT_VISUAL_CLASS_NAME}>
            <ProductVisual product={previousProduct} variant="left" />
          </div>
          <button
            type="button"
            aria-label={normalizeConfiguredText(activeProduct.short_name || activeProduct.name)}
            className={`${CENTER_PRODUCT_VISUAL_CLASS_NAME} cursor-pointer bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-purple`}
            onClick={handleProductClick}
          >
            <ProductVisual product={activeProduct} variant="center" />
          </button>
          <div className={SIDE_PRODUCT_VISUAL_CLASS_NAME}>
            <ProductVisual product={nextProduct} variant="right" />
          </div>
        </div>
      </div>

      <div className={PRODUCT_FOOTER_CLASS_NAME}>
        <div className="min-w-0">
          <h3 className="truncate font-br text-[16px] leading-[19px] text-[#6c6c6c]">
            {normalizeConfiguredText(activeProduct.short_name || activeProduct.name)}
          </h3>
          <div className="mt-[6px] flex items-baseline gap-[6px] whitespace-nowrap">
            <AccessiblePrice
              value={sellPrice}
              label=""
              className="font-bb text-[18px] leading-[18px] text-primary-black"
            />
            {originalPrice ? (
              <AccessiblePrice
                as="del"
                value={originalPrice}
                label=""
                className="font-br text-[16px] leading-[19px] text-[#6c6c6c]"
              />
            ) : null}
          </div>
          {rankingText ? (
            <span className="mt-[8px] inline-flex h-[20px] max-w-[220px] items-center gap-[4px] rounded-[2px] bg-[#d9e2ff] px-[6px] py-[3px] font-br text-[12px] leading-[14px] text-[#44427a]">
              <Image
                src={rankingLabelIcon}
                alt=""
                width={16}
                height={16}
                aria-hidden
                className="size-[16px] shrink-0"
              />
              <span className="truncate">{rankingText}</span>
            </span>
          ) : null}
        </div>

        <button
          type="button"
          className="ml-[24px] h-[40px] w-[152px] shrink-0 cursor-pointer rounded-[20px] bg-primary-black px-[16px] font-bb text-[14px] leading-[17px] text-white transition-colors hover:bg-primary-black-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple"
          onClick={handleProductClick}
        >
          {t('shopNow')}
        </button>
      </div>

      <button
        type="button"
        data-carousel-direction="previous"
        aria-label={normalizeConfiguredText(previousProduct.short_name || previousProduct.name)}
        className={`${CAROUSEL_BUTTON_CLASS_NAME} left-[24px] xl:left-[40px] 3xl:left-[56px]`}
        onClick={() => setCurrentIndex((index) => (index - 1 + products.length) % products.length)}
      >
        <span className="flex size-[20px] rotate-180 items-center justify-center">
          <RightArrowFineOutlined className="text-[20px] [&_path]:stroke-white" aria-hidden />
        </span>
      </button>
      <button
        type="button"
        data-carousel-direction="next"
        aria-label={normalizeConfiguredText(nextProduct.short_name || nextProduct.name)}
        className={`${CAROUSEL_BUTTON_CLASS_NAME} right-[24px] xl:right-[40px] 3xl:right-[56px]`}
        onClick={() => setCurrentIndex((index) => (index + 1) % products.length)}
      >
        <span className="flex size-[20px] items-center justify-center">
          <RightArrowFineOutlined className="text-[20px] [&_path]:stroke-white" aria-hidden />
        </span>
      </button>
    </section>
  );
}

export default function Bestsellers2026(props: Bestsellers2026Props) {
  const content = props.indexData.content ?? props.indexData;
  const configuredTabs = Array.isArray(content.product_list) ? content.product_list : [];
  const tabs = configuredTabs.filter(
    (tab) =>
      Number(tab.product_group_id || tab.position_jump_product_group?.id) > 0 &&
      isRankingLabel(tab.ranking_label) &&
      (configuredTabs.length === 1 || Boolean(normalizeConfiguredText(tab.list_title))),
  );

  if (Number(content.product_card_style) !== 1 || tabs.length === 0) return null;

  return <Bestsellers2026ContentComponent {...props} content={content} tabs={tabs} />;
}
